import React, { useEffect } from 'react';
import type { AssignChannel } from '../../types/assignment';
import { usePlanFeatures } from '@/hooks/usePlanFeatures';

interface ChannelSelectProps {
  value: AssignChannel[];
  onChange: (channels: AssignChannel[]) => void;
  disabled?: boolean;
}

const FONT = 'Poppins, sans-serif';

const MailIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="2" />
    <path d="M4 7l8 6 8-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChatIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M4 5h16v11H8l-4 3V5z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CheckIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const OPTIONS: { value: AssignChannel; label: string; icon: React.ReactNode }[] = [
  { value: 'EMAIL', label: 'Email', icon: <MailIcon /> },
  { value: 'CHAT', label: 'Tereiko Chat', icon: <ChatIcon /> },
];

/**
 * ChannelSelect — multi-select notification channel toggles (Email / Tereiko
 * Chat). At least one channel is always kept selected (the last one can't be
 * removed). Shared by the course and program configure wizards.
 */
function ChannelSelect({ value, onChange, disabled = false }: ChannelSelectProps) {
  // Invitation emails are a paid row (Growth+). The backend simply skips the
  // send on plans without it, so offering the toggle here meant a trainer could
  // "send by email" and have nothing leave the building (Aleix's ripple rule,
  // 2026-09-08). Locked with its plan named instead.
  const { invitationEmails } = usePlanFeatures();

  const isLocked = (channel: AssignChannel) => channel === 'EMAIL' && !invitationEmails;

  // Never leave a locked channel selected — chat is always available.
  useEffect(() => {
    if (!invitationEmails && value.includes('EMAIL')) {
      const next = value.filter((c) => c !== 'EMAIL');
      onChange(next.length > 0 ? next : ['CHAT']);
    }
  }, [invitationEmails, value, onChange]);

  const toggle = (channel: AssignChannel) => {
    if (disabled || isLocked(channel)) return;
    if (value.includes(channel)) {
      if (value.length === 1) return; // keep at least one selected
      onChange(value.filter((c) => c !== channel));
    } else {
      // Preserve OPTIONS order for a stable payload.
      onChange(OPTIONS.map((o) => o.value).filter((c) => c === channel || value.includes(c)));
    }
  };

  return (
    <div className="flex flex-wrap items-center justify-center gap-3" role="group" aria-label="Notification channels">
      {OPTIONS.map((opt) => {
        const active = value.includes(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => toggle(opt.value)}
            disabled={disabled || isLocked(opt.value)}
            aria-pressed={active}
            title={isLocked(opt.value) ? 'Invitation emails are available from the Growth plan' : undefined}
            className={`inline-flex items-center gap-2 h-11 px-4 rounded-full border text-sm font-bold transition-[background-color,border-color,color,transform] duration-150 active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100 ${
              active
                ? 'border-primary-500 bg-primary-500/10 text-primary-600 dark:text-primary-300'
                : 'border-text-primary/20 text-text-secondary hover:border-text-primary/40'
            }`}
            style={{ fontFamily: FONT }}
          >
            <span
              className={`flex h-[18px] w-[18px] items-center justify-center rounded-md border transition-colors ${ active ? 'border-primary-500 bg-primary-500 ' : 'border-text-primary/30 text-transparent' }`}
            >
              {active && <CheckIcon />}
            </span>
            {opt.icon}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

export default ChannelSelect;
